import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { X, Goal, Star, Ticket, Trophy, Loader2, ListChecks } from "lucide-react";

// Lets a user attach a match, pick, bet or competition to a chat message.
export default function ChatContextPicker({ open, onClose, onPick }) {
  const [tab, setTab] = useState("fixture");
  const [comps, setComps] = useState([]);
  const [selectedComp, setSelectedComp] = useState(null);
  const [fixtures, setFixtures] = useState([]);
  const [picks, setPicks] = useState([]);
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pollFixtureComp, setPollFixtureComp] = useState(null);
  const [pollFixtures, setPollFixtures] = useState([]);
  const [customQ, setCustomQ] = useState("");
  const [customOpts, setCustomOpts] = useState(["", ""]);

  useEffect(() => {
    if (!open) return;
    api.get("/tournaments").then((r) => setComps(r.data)).catch(() => setComps([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (tab === "pick" && picks.length === 0) {
      setLoading(true);
      api.get("/best-bets").then((r) => setPicks(r.data.picks || [])).catch(() => setPicks([])).finally(() => setLoading(false));
    }
    if (tab === "bet" && bets.length === 0) {
      setLoading(true);
      api.get("/bets").then((r) => setBets(r.data)).catch(() => setBets([])).finally(() => setLoading(false));
    }
  }, [tab, open]); // eslint-disable-line

  const loadFixtures = async (comp) => {
    setSelectedComp(comp);
    setLoading(true);
    try {
      const r = await api.get(`/tournaments/${encodeURIComponent(comp.name)}/fixtures`);
      setFixtures(r.data.fixtures || []);
    } catch { setFixtures([]); } finally { setLoading(false); }
  };

  const loadPollFixtures = async (comp) => {
    setPollFixtureComp(comp);
    setLoading(true);
    try {
      const r = await api.get(`/tournaments/${encodeURIComponent(comp.name)}/fixtures`);
      setPollFixtures(r.data.fixtures || []);
    } catch { setPollFixtures([]); } finally { setLoading(false); }
  };

  if (!open) return null;

  const Row = ({ children, onClick, testid }) => (
    <button data-testid={testid} onClick={onClick}
      className="w-full text-left p-3 rounded-2xl hover:bg-secondary transition-colors flex items-center gap-3">
      {children}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" data-testid="context-picker" onClick={onClose}>
      <div className="glass w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-4 space-y-3 max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <span className="font-display font-extrabold">Add to your message</span>
          <Button variant="ghost" size="icon" className="rounded-full" onClick={onClose} data-testid="picker-close"><X className="h-4 w-4" /></Button>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid grid-cols-5 rounded-full">
            <TabsTrigger value="fixture" data-testid="tab-fixture" className="rounded-full text-xs gap-1"><Goal className="h-3.5 w-3.5" />Match</TabsTrigger>
            <TabsTrigger value="pick" data-testid="tab-pick" className="rounded-full text-xs gap-1"><Star className="h-3.5 w-3.5" />Pick</TabsTrigger>
            <TabsTrigger value="bet" data-testid="tab-bet" className="rounded-full text-xs gap-1"><Ticket className="h-3.5 w-3.5" />Bet</TabsTrigger>
            <TabsTrigger value="competition" data-testid="tab-competition" className="rounded-full text-xs gap-1"><Trophy className="h-3.5 w-3.5" />Comp</TabsTrigger>
            <TabsTrigger value="poll" data-testid="tab-poll" className="rounded-full text-xs gap-1"><ListChecks className="h-3.5 w-3.5" />Poll</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto mt-3 min-h-[200px]">
            {loading && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}

            <TabsContent value="fixture" className="m-0 space-y-1">
              {!selectedComp ? (
                comps.map((c) => (
                  <Row key={c.name} testid={`picker-comp-${c.name}`} onClick={() => loadFixtures(c)}>
                    {c.league?.logo ? <img src={c.league.logo} alt="" className="h-6 w-6 object-contain" /> : <Trophy className="h-5 w-5 opacity-60" />}
                    <span className="font-medium text-sm">{c.name}</span>
                  </Row>
                ))
              ) : (
                <>
                  <button className="text-xs text-primary font-medium mb-1 px-1" onClick={() => { setSelectedComp(null); setFixtures([]); }}>{"< Back to competitions"}</button>
                  {!loading && fixtures.map((f) => (
                    <Row key={f.id} testid={`picker-fixture-${f.id}`}
                      onClick={() => onPick({ type: "fixture", fixture_id: f.id, home: f.home.name, away: f.away.name, league: f.league?.name, tournament: selectedComp.name, date: f.date })}>
                      <Goal className="h-5 w-5 text-primary shrink-0" />
                      <div>
                        <div className="font-medium text-sm">{f.home.name} vs {f.away.name}</div>
                        <div className="text-[11px] text-muted-foreground">{new Date(f.date).toLocaleString()}</div>
                      </div>
                    </Row>
                  ))}
                  {!loading && fixtures.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No upcoming fixtures.</p>}
                </>
              )}
            </TabsContent>

            <TabsContent value="pick" className="m-0 space-y-1">
              {!loading && picks.slice(0, 40).map((p) => (
                <Row key={`${p.fixture_id}-${p.player}-${p.prop_key}`} testid={`picker-pick-${p.fixture_id}-${p.prop_key}`}
                  onClick={() => onPick({ type: "pick", player: p.player, prop: p.prop, pct: p.recent?.pct, odd: p.odd, matchup: p.matchup, tournament: p.tournament, fixture_id: p.fixture_id })}>
                  <Star className="h-5 w-5 text-primary shrink-0" />
                  <div>
                    <div className="font-medium text-sm">{p.player} · {p.prop}</div>
                    <div className="text-[11px] text-muted-foreground">{p.recent?.pct}% chance · {p.tournament}</div>
                  </div>
                </Row>
              ))}
              {!loading && picks.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No picks available right now.</p>}
            </TabsContent>

            <TabsContent value="bet" className="m-0 space-y-1">
              {!loading && bets.map((b) => (
                <Row key={b.id} testid={`picker-bet-${b.id}`}
                  onClick={() => onPick({ type: "bet", market: b.market, selection: b.selection, odds: b.odds, stake: b.stake, result: b.result, teams: b.home_team && b.away_team ? `${b.home_team} vs ${b.away_team}` : b.match })}>
                  <Ticket className="h-5 w-5 text-primary shrink-0" />
                  <div>
                    <div className="font-medium text-sm">{b.selection || b.market}</div>
                    <div className="text-[11px] text-muted-foreground">{b.market} · @{Number(b.odds || 0).toFixed(2)} · {b.result}</div>
                  </div>
                </Row>
              ))}
              {!loading && bets.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">You have no bets to share yet.</p>}
            </TabsContent>

            <TabsContent value="competition" className="m-0 space-y-1">
              {comps.map((c) => (
                <Row key={c.name} testid={`picker-competition-${c.name}`}
                  onClick={() => onPick({ type: "competition", name: c.name, logo: c.league?.logo })}>
                  {c.league?.logo ? <img src={c.league.logo} alt="" className="h-6 w-6 object-contain" /> : <Trophy className="h-5 w-5 opacity-60" />}
                  <span className="font-medium text-sm">{c.name}</span>
                </Row>
              ))}
              {comps.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No competitions yet.</p>}
            </TabsContent>

            <TabsContent value="poll" className="m-0 space-y-4">
              <div>
                <div className="text-xs font-semibold text-muted-foreground mb-1.5">Match poll (Home / Draw / Away)</div>
                {!pollFixtureComp ? (
                  <div className="space-y-1">
                    {comps.map((c) => (
                      <Row key={c.name} testid={`pollcomp-${c.name}`} onClick={() => loadPollFixtures(c)}>
                        {c.league?.logo ? <img src={c.league.logo} alt="" className="h-6 w-6 object-contain" /> : <Trophy className="h-5 w-5 opacity-60" />}
                        <span className="font-medium text-sm">{c.name}</span>
                      </Row>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <button className="text-xs text-primary font-medium mb-1 px-1" onClick={() => { setPollFixtureComp(null); setPollFixtures([]); }}>{"< Back"}</button>
                    {!loading && pollFixtures.map((f) => (
                      <Row key={f.id} testid={`pollfixture-${f.id}`}
                        onClick={() => onPick({ type: "poll", fixture: { home: f.home.name, away: f.away.name, fixture_id: f.id, tournament: pollFixtureComp.name } })}>
                        <ListChecks className="h-5 w-5 text-primary shrink-0" />
                        <div>
                          <div className="font-medium text-sm">{f.home.name} vs {f.away.name}</div>
                          <div className="text-[11px] text-muted-foreground">Who wins?</div>
                        </div>
                      </Row>
                    ))}
                  </div>
                )}
              </div>
              <div className="border-t border-border pt-3">
                <div className="text-xs font-semibold text-muted-foreground mb-1.5">Custom poll</div>
                <Input value={customQ} onChange={(e) => setCustomQ(e.target.value)} placeholder="Ask a question" className="rounded-xl mb-2" data-testid="poll-question-input" />
                {customOpts.map((o, i) => (
                  <Input key={i} value={o} data-testid={`poll-opt-${i}`}
                    onChange={(e) => setCustomOpts((prev) => prev.map((v, j) => j === i ? e.target.value : v))}
                    placeholder={`Option ${i + 1}`} className="rounded-xl mb-1.5" />
                ))}
                <div className="flex items-center gap-2 mt-1">
                  {customOpts.length < 6 && (
                    <Button variant="ghost" size="sm" className="rounded-full text-xs" onClick={() => setCustomOpts((p) => [...p, ""])} data-testid="poll-add-option">+ Option</Button>
                  )}
                  <Button size="sm" className="rounded-full text-xs ml-auto" data-testid="poll-create-custom"
                    disabled={!customQ.trim() || customOpts.filter((o) => o.trim()).length < 2}
                    onClick={() => onPick({ type: "poll", question: customQ.trim(), options: customOpts.filter((o) => o.trim()) })}>
                    Add poll
                  </Button>
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
